// components/SettingsPopup.js
import { useState, useEffect } from 'react';

const SettingsPopup = ({ isOpen, onClose, onSettingsChange, onOpenNewVideo }) => {
  // Estados de configuración
  const [timeMinutes, setTimeMinutes] = useState(() => {
    const savedTimeLimit = localStorage.getItem('timeLimit');
    return savedTimeLimit ? parseInt(savedTimeLimit) : 30;
  });
  const [languageMode, setLanguageMode] = useState(() => localStorage.getItem('languageMode') || 't');
  const [filterMode, setFilterMode] = useState(() => localStorage.getItem('filterMode') || 'v');
  const [debugMode, setDebugMode] = useState(() => localStorage.getItem('debugMode') === 'true');
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [tagsInclude, setTagsInclude] = useState('');
  const [tagsExclude, setTagsExclude] = useState('');
  
  // Estado para colecciones disponibles
  const [availableCollections, setAvailableCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);

  // Cargar colecciones disponibles
  useEffect(() => {
    if (isOpen) {
      fetchCollections();
    }
  }, [isOpen]);

  // Cargar filtros guardados
  useEffect(() => {
    if (isOpen) {
      const savedCollections = localStorage.getItem('selectedCollections');
      const savedTagsInclude = localStorage.getItem('tagsInclude');
      const savedTagsExclude = localStorage.getItem('tagsExclude');
      
      if (savedCollections) setSelectedCollections(JSON.parse(savedCollections));
      if (savedTagsInclude) setTagsInclude(savedTagsInclude);
      if (savedTagsExclude) setTagsExclude(savedTagsExclude);
    }
  }, [isOpen]);

  const fetchCollections = async () => {
    setLoadingCollections(true);
    try {
      const response = await fetch('https://videokids.visssible.com/backend/get-collections.php');
      const result = await response.json();
      if (result.success) {
        setAvailableCollections(result.collections);
      }
    } catch (err) {
      console.error('Error cargando colecciones:', err);
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleSaveSettings = () => {
    // Guardar time en localStorage (en minutos)
    if (timeMinutes && timeMinutes > 0) {
      const timeInSeconds = timeMinutes * 60;
      localStorage.setItem('timeLimit', timeMinutes.toString());
      localStorage.setItem('timeLeft', timeInSeconds.toString());
      const today = new Date().toDateString();
      localStorage.setItem('lastResetDate', today);
    }
    
    // Guardar otros settings
    localStorage.setItem('languageMode', languageMode);
    localStorage.setItem('filterMode', filterMode);
    localStorage.setItem('debugMode', debugMode.toString());
    localStorage.setItem('selectedCollections', JSON.stringify(selectedCollections));
    localStorage.setItem('tagsInclude', tagsInclude);
    localStorage.setItem('tagsExclude', tagsExclude);
    
    // Notificar cambios
    if (onSettingsChange) {
      onSettingsChange({
        timeMinutes,
        languageMode,
        filterMode,
        debugMode,
        selectedCollections,
        tagsInclude,
        tagsExclude
      });
    }
    
    // Disparar evento para recargar catálogo
    window.dispatchEvent(new CustomEvent('localStorageChange'));
    
    onClose();
  };

  const handleCollectionToggle = (collection) => {
    setSelectedCollections(prev => {
      if (prev.includes(collection)) {
        return prev.filter(c => c !== collection);
      } else {
        return [...prev, collection];
      }
    });
  };

  const handleSelectAllCollections = () => {
    if (selectedCollections.length === availableCollections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections([...availableCollections]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        
        {/* Header - z-index fix */}
        <div className="sticky top-0 z-10 bg-gray-800 px-6 py-4 border-b border-gray-700 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">
            <i className="fa fa-cog mr-2"></i>
            Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-5">
            {/* Row 1: Time, Language & Mode */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Time (minutes)
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={timeMinutes}
                  onChange={(e) => setTimeMinutes(parseInt(e.target.value) || 0)}
                  min="1"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Language
                </label>
                <select
                  value={languageMode}
                  onChange={(e) => setLanguageMode(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="es">Spanish</option>
                  <option value="en">English</option>
                  <option value="t">All</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Mode
                </label>
                <select
                  value={filterMode}
                  onChange={(e) => setFilterMode(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="v">Videos</option>
                  <option value="m">Music</option>
                  <option value="r">Radio</option>
                </select>
              </div>
            </div>

            {/* Row 2: Collections */}
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Collections
              </label>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 max-h-40 overflow-y-auto">
                <label className="flex items-center p-2 hover:bg-gray-700 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCollections.length === availableCollections.length && availableCollections.length > 0}
                    onChange={handleSelectAllCollections}
                    className="mr-2"
                  />
                  <span className="text-white text-sm">Select All</span>
                </label>
                {loadingCollections ? (
                  <div className="text-gray-400 text-sm p-2">Loading...</div>
                ) : (
                  availableCollections.map(collection => (
                    <label key={collection} className="flex items-center p-2 hover:bg-gray-700 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCollections.includes(collection)}
                        onChange={() => handleCollectionToggle(collection)}
                        className="mr-2"
                      />
                      <span className="text-white text-sm">{collection}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Row 3: Show Tags & Exclude Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Show Tags
                </label>
                <input
                  type="text"
                  value={tagsInclude}
                  onChange={(e) => setTagsInclude(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Exclude Tags
                </label>
                <input
                  type="text"
                  value={tagsExclude}
                  onChange={(e) => setTagsExclude(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Row 4: Debug Mode & Add Video */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <label className="text-gray-300 text-sm font-medium">
                  Debug Mode
                </label>
                <button
                  onClick={() => setDebugMode(!debugMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    debugMode ? 'bg-yellow-500' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      debugMode ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <button
                onClick={() => {
                  if (onOpenNewVideo) onOpenNewVideo();
                  onClose();
                }}
                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <i className="fa fa-plus"></i>
                Add Video
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-700">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPopup;